import type {
    PublishedColumnModel,
    PublishedColumnScope,
    PublishedRecordModel,
    PublishedReviewListModel,
    PublishedRowModel,
    PublishedSnapshotModel,
    PublishedTableModel,
} from '../../types/publishedReview.types';
import { associationsData, recordsData } from './climateAssociationsData';

// Mock data for a single published review, built from a real climate–disease
// association extraction (see climateAssociationsData.ts, generated from
// extractions.csv). See docs/design/structured-extraction-and-publishing.md
// for the model.

// Deterministic GUID-format ids so links survive reloads. The prefix keeps
// each entity type's ids distinct.
const mockGuid = (prefix: number, n: number) =>
    `${String(prefix).padStart(8, '0')}-0000-4000-8000-${String(n).padStart(12, '0')}`;

export const MOCK_REVIEW_ID = mockGuid(0, 1);
const PUBLISHED_AT = '2026-10-02T09:00:00.000Z';

// ---------------------------------------------------------------------------
// Authoring-side reference data. Not served by any public endpoint — kept to
// show what the published data below was derived from.
// ---------------------------------------------------------------------------

const nullableString = (title: string, description?: string) => ({
    type: ['string', 'null'],
    title,
    ...(description && { description }),
});

/** ExtractionSchema.JsonSchema — the user-defined shape of each record's extraction. */
export const mockExtractionSchema = {
    id: mockGuid(1, 1),
    reviewId: MOCK_REVIEW_ID,
    version: 1,
    name: 'Impact of Climate Change on Rift Valley Fever Virus: A Focus on Rainfall',
    jsonSchema: {
        $schema: 'https://json-schema.org/draft/2020-12/schema',
        title: 'Impact of Climate Change on Rift Valley Fever Virus: A Focus on Rainfall',
        type: 'object',
        properties: {
            disease: nullableString('Disease'),
            environmental_variable: nullableString('Environmental variable'),
            case_definition: nullableString('Case definition'),
            case_source: nullableString('Case source'),
            maintenance_host: nullableString('Maintenance host'),
            disease_vector: nullableString('Disease vector'),
            vector_species: nullableString('Vector species'),
            measure: nullableString('Measure'),
            variable: nullableString('Variable', 'Climate variable the associations are reported against.'),
            country: nullableString('Country', 'Country where the study was conducted.'),
            location: nullableString('Location', 'Sub-national study area(s), as named in the source.'),
            study_type: nullableString('Study type'),
            study_period: nullableString('Study period', 'Start and end of data collection, as written.'),
            supplementary_material: nullableString('Supplementary material'),
            sample_size: nullableString('Sample size'),
            data_sources: {
                type: 'array',
                title: 'Data sources',
                description: 'Each distinct source of climate, vector or case data.',
                items: {
                    type: 'object',
                    properties: { name: { type: 'string', title: 'Name' } },
                    required: ['name'],
                },
            },
            associations: {
                type: 'array',
                title: 'Associations',
                description: 'One entry per reported association between an environmental variable and a disease outcome. Report each statistical result separately.',
                items: {
                    type: 'object',
                    properties: {
                        asso_group: { type: 'string', title: 'Association group', description: "What is being associated, in the source's words." },
                        asso_type: nullableString('Association type', 'Statistical method, e.g. GLM estimate, cross-correlation.'),
                        value: { type: ['number', 'string', 'null'], title: 'Value', description: "Numeric result, or 'graph' if only shown graphically." },
                        unit: nullableString('Unit'),
                        uncertainty_type: nullableString('Uncertainty type', 'e.g. SE, SD, 95% CI, p-value.'),
                        uncertainty_value: nullableString('Uncertainty value'),
                        reference_description: nullableString('Reference description', 'Reference category or baseline the value is relative to.'),
                    },
                    required: ['asso_group'],
                },
            },
        },
        required: ['associations'],
    },
};

type ProjectionColumn = {
    key: string;
    label: string;
    from: string;
    aggregate?: 'join' | 'count' | 'first' | 'min' | 'max' | 'sum';
    separator?: string;
};

/** PublishProjection — flattens each record's extraction into one row per association. */
export const mockAssociationsProjection: {
    id: string;
    reviewId: string;
    schemaId: string;
    name: string;
    definition: { rows: string; emptyRecords: 'omit' | 'singleRow'; columns: ProjectionColumn[] };
} = {
    id: mockGuid(2, 1),
    reviewId: MOCK_REVIEW_ID,
    schemaId: mockExtractionSchema.id,
    name: 'Associations',
    definition: {
        rows: '$.associations[*]',
        emptyRecords: 'omit',
        columns: [
            { key: 'source', label: 'Source', from: 'record.citation' },
            { key: 'disease', label: 'Disease', from: '$.disease' },
            { key: 'environmental_variable', label: 'Environmental variable', from: '$.environmental_variable' },
            { key: 'case_definition', label: 'Case definition', from: '$.case_definition' },
            { key: 'case_source', label: 'Case source', from: '$.case_source' },
            { key: 'maintenance_host', label: 'Maintenance host', from: '$.maintenance_host' },
            { key: 'disease_vector', label: 'Disease vector', from: '$.disease_vector' },
            { key: 'vector_species', label: 'Vector species', from: '$.vector_species' },
            { key: 'country', label: 'Country', from: '$.country' },
            { key: 'location', label: 'Location', from: '$.location' },
            { key: 'data_source', label: 'Data source', from: '$.data_sources[*].name', aggregate: 'join', separator: '; ' },
            { key: 'measure', label: 'Measure', from: '$.measure' },
            { key: 'variable', label: 'Variable', from: '$.variable' },
            { key: 'study_type', label: 'Study type', from: '$.study_type' },
            { key: 'study_period', label: 'Study period', from: '$.study_period' },
            { key: 'supplementary_material', label: 'Supplementary material', from: '$.supplementary_material' },
            { key: 'sample_size', label: 'Sample size', from: '$.sample_size' },
            { key: 'asso_group', label: 'Association group', from: '@.asso_group' },
            { key: 'asso_type', label: 'Association type', from: '@.asso_type' },
            { key: 'value', label: 'Value', from: '@.value' },
            { key: 'unit', label: 'Unit', from: '@.unit' },
            { key: 'uncertainty_type', label: 'Uncertainty type', from: '@.uncertainty_type' },
            { key: 'uncertainty_value', label: 'Uncertainty value', from: '@.uncertainty_value' },
            { key: 'reference_description', label: 'Reference description', from: '@.reference_description' },
        ],
    },
};

// ---------------------------------------------------------------------------
// Public, published data — what the /published endpoints return.
// ---------------------------------------------------------------------------

export const mockPublishedRecords: PublishedRecordModel[] =
    recordsData.map((record, i) => ({ id: mockGuid(3, i + 1), ...record }));

const scopeOf = (from: string): PublishedColumnScope =>
    from.startsWith('record.') ? 'metadata' : from.startsWith('$.') ? 'record' : 'item';

/** PublishedTable.Columns — resolved from the projection; every value is published as text. */
export const mockAssociationsColumns: PublishedColumnModel[] =
    mockAssociationsProjection.definition.columns.map(({ key, label, from }) => ({ key, label, type: 'string', scope: scopeOf(from) }));

/**
 * Columns whose values come from the record's live metadata (`record.*`
 * paths) rather than being stored on the row — the rows endpoint fills them in.
 */
export const mockRecordColumns: { key: string; field: keyof PublishedRecordModel }[] =
    mockAssociationsProjection.definition.columns
        .filter(column => column.from.startsWith('record.'))
        .map(column => ({ key: column.key, field: column.from.slice('record.'.length) as keyof PublishedRecordModel }));

/** PublishedRow — extracted values only; `record.*` columns are not stored. */
export const mockAssociationsRows: PublishedRowModel[] = associationsData.map(([recordIndex, values], i) => ({
    id: mockGuid(6, i + 1),
    recordId: mockPublishedRecords[recordIndex].id,
    values,
}));

export const mockAssociationsTable: PublishedTableModel = {
    id: mockGuid(4, 1),
    name: mockAssociationsProjection.name,
    rowCount: mockAssociationsRows.length,
};

export const mockPublishedSnapshot: PublishedSnapshotModel = {
    id: mockGuid(5, 1),
    version: 1,
    publishedAt: PUBLISHED_AT,
    tables: [mockAssociationsTable],
};

export const mockPublishedReview: PublishedReviewListModel = {
    id: MOCK_REVIEW_ID,
    title: 'Impact of Climate Change on Rift Valley Fever Virus: A Focus on Rainfall',
    disease: 'RVF',
    climateVariable: 'Rainfall',
    studyCount: mockPublishedRecords.length,
    parameterCount: mockAssociationsRows.length,
    lastModified: PUBLISHED_AT,
};
